<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("TAXES.TAXES_LIST") }}</h3>
          </div>

          <div class="col-6 text-right">
            <base-button
              class="elite-button btn-black add"
              icon
              size="sm"
              @click="openTaxGroups"
              v-if="$currentUserCan($permissions.PERM_VIEW_TAXES)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-folder"></i>
              </span>
              <span class="btn-inner--text">
                {{ $t("TAXES.TAX_GROUPS") }}
              </span>
            </base-button>
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openTaxCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_TAXES)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">
                {{ $t("TAXES.ADD_TAX") }}
              </span>
            </base-button>

            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_TAXES)"
              :objectType="'taxes'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <tax-list-table
        @onViewTax="openTaxViewModal"
        @onEditTax="openTaxEditModal"
        @onDeleteTax="deleteTax"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewTaxModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewTaxModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'TAX_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("TAXES.VIEW_TAX") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openTax"
                :objectType="'taxes'"
                :objectId="openTax.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />

              <button class="close" @click="closeTaxModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-tax-component
              v-if="openTax"
              :taxId="openTax.id"
              @onEditTax="openTaxEditModal"
              @onDeleteTax="deleteTax"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isEditTaxModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditTaxModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'TAX'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("TAXES.EDIT_TAX") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button class="view" @click="openTaxViewModal(openTax)">
                  <i class="fal fa-expand-alt"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeTaxModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-tax-component
              v-if="openTax"
              :taxId="openTax.id"
              @onViewTax="openTaxViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddTaxModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddTaxModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'TAX'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("TAXES.ADD_TAX") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeTaxModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-tax-component @onViewTax="openTaxViewModal" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import TaxListTable from "./partials/TaxListTable.vue";
import EditTaxComponent from "./components/EditTaxComponent.vue";
import AddTaxComponent from "./components/AddTaxComponent.vue";
import ViewTaxComponent from "./components/ViewTaxComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    TaxListTable,
    NotificationSubscription,
    EditTaxComponent,
    AddTaxComponent,
    ViewTaxComponent,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  computed: {},

  watch: {},

  data() {
    const taxId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewTaxModalOpened = false;
    let isEditTaxModalOpened = false;
    let isAddTaxModalOpened = false;
    let openTax = null;
    if (taxId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewTaxModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditTaxModalOpened = true;
      }
      openTax = { id: taxId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddTaxModalOpened = true;
    }
    return {
      isViewTaxModalOpened: isViewTaxModalOpened,
      isEditTaxModalOpened: isEditTaxModalOpened,
      isAddTaxModalOpened: isAddTaxModalOpened,
      openTax: openTax,
      renderKey: 1,
    };
  },

  methods: {
    openTaxCreateModal() {
      this.closeTaxModal();
      this.isAddTaxModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Taxes",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openTaxGroups() {
      this.$router.push({
        name: "List TaxGroups",
      });
    },
    openTaxViewModal(tax, reRender = false) {
      this.closeTaxModal();
      this.openTax = tax;
      this.isViewTaxModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Taxes",
          query: { id: this.openTax.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },
    openTaxEditModal(tax) {
      this.closeTaxModal();
      this.openTax = tax;
      this.isEditTaxModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Taxes",
          query: { id: this.openTax.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    closeTaxModal() {
      this.isAddTaxModalOpened = false;
      this.isViewTaxModalOpened = false;
      this.isEditTaxModalOpened = false;
      this.openTax = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Taxes",
          query: {},
        }).href
      );
    },

    async deleteTax(tax) {
      const confirmation = await swal.fire({
        text: this.$t("TAXES.DELETE_THIS_TAX"),
        type: "question",
        customClass: {
          popup: "delete-popup",
        },
        buttonsStyling: false,
        showCancelButton: true,
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonText:
          this.$t("COMMON.YES") + ", " + this.$t("COMMON.DELETE"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("taxes/destroy", tax.id);
          this.renderKey++;
          this.closeTaxModal();
          this.$notify({
            type: "success",
            message: this.$t("TAXES.TAX_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
